import React, { createContext, useContext, useMemo, useState, useSyncExternalStore } from 'react';
import { reauthEmCurso, subscribe as subscribeReauth } from 'services/main/reauthStore';

/**
 * Estado TRANSITÓRIO "a modal de update está NA TELA agora" — não é "update pendente"
 * (que duraria a sessão). Fonte única entre o VersionChecker (escreve) e o Amplifique
 * (lê, pra não colar o survey NPS na modal). Escopo estreito: só esses dois consomem,
 * então mudar o flag re-renderiza só os consumidores, não a árvore.
 */
type UpdateContextValue = {
  modalUpdateNaTela: boolean;
  setModalUpdateNaTela: (value: boolean) => void;
  /** A re-autenticação está na tela (MARINERS-2011). Prioridade máxima: sem sessão não
   *  há o que atualizar nem sobre o que opinar numa pesquisa. */
  reauthNaTela: boolean;
  /** "Tem alguém interrompendo o usuário agora?" — a pergunta que o próximo ator (NPS)
   *  precisa fazer, sem ter que conhecer cada interruptor individualmente. */
  interrupcaoNaTela: boolean;
};

export const UpdateContext = createContext<UpdateContextValue>({
  modalUpdateNaTela: false,
  setModalUpdateNaTela: () => {},
  reauthNaTela: false,
  interrupcaoNaTela: false,
});

export const UpdateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalUpdateNaTela, setModalUpdateNaTela] = useState(false);

  /* `reauthNaTela` é DERIVADO do store, não publicado por quem monta a modal.
   *
   * Publicar exigiria que o Host escrevesse aqui num efeito, e aí o flag chegaria aos
   * consumidores um passo DEPOIS da modal existir. `useSyncExternalStore` lê o valor
   * autoritativo no mesmo render — se a queda de sessão e a detecção de versão caírem no
   * mesmo tick, o gate já está de pé quando o VersionChecker decide o que renderizar.
   *
   * É a mesma lição da corrida do Amplifique (re-checar no PONTO do efeito, não só na
   * entrada), aplicada de forma que a corrida nem chega a existir. */
  const reauthNaTela = useSyncExternalStore(subscribeReauth, reauthEmCurso);

  // value memoizado: só muda quando algum flag muda (evita re-render por identidade nova).
  const value = useMemo(
    () => ({
      modalUpdateNaTela,
      setModalUpdateNaTela,
      reauthNaTela,
      interrupcaoNaTela: modalUpdateNaTela || reauthNaTela,
    }),
    [modalUpdateNaTela, reauthNaTela],
  );
  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>;
};

export const useUpdateModalState = () => useContext(UpdateContext);
